import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { supabase } from '@/integrations/supabase/client';
import { 
  Search, 
  MapPin, 
  Users, 
  Globe,
  Filter,
  School,
  Building,
  Clock,
  Star,
  ExternalLink,
  TrendingUp,
  Award,
  BookOpen,
  GraduationCap
} from 'lucide-react';

interface SelectiveSchool {
  id?: string;
  name: string;
  location: string;
  type: 'fully_selective' | 'partially_selective' | 'agricultural';
  gender: 'boys' | 'girls' | 'coeducational';
  region: string;
  established_year?: number;
  website?: string;
  subjects?: string[];
  notable_features?: string[];
}

export const SelectiveSchoolsFinder: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedGender, setSelectedGender] = useState('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectiveSchools, setSelectiveSchools] = useState<SelectiveSchool[]>([]);
  const [loading, setLoading] = useState(true);

  const ITEMS_PER_PAGE = 10;

  // Fetch schools from database
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const { data, error } = await supabase
          .from('selective_schools')
          .select('id, name, type, region, gender, location, campus, coed, school_type, cutoff_2024, hsc_rank, website, notable_features, subjects')
          .order('name');
        
        if (error) throw error;
        setSelectiveSchools(data || []);
      } catch (error) {
        console.error('Error fetching selective schools:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSchools();
  }, []);

  const regions = useMemo(() => 
    Array.from(new Set(selectiveSchools.map(school => school.region))).sort(),
    [selectiveSchools]
  );

  const schoolTypes = useMemo(() => 
    Array.from(new Set(selectiveSchools.map(school => school.type))).sort(),
    [selectiveSchools]
  );

  const filteredSchools = useMemo(() => {
    return selectiveSchools.filter(school => {
      const matchesSearch = school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           school.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           school.region.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === 'all' || school.region === selectedRegion;
      const matchesType = selectedType === 'all' || school.type === selectedType;
      const matchesGender = selectedGender === 'all' || school.gender === selectedGender;
      
      return matchesSearch && matchesRegion && matchesType && matchesGender;
    });
  }, [searchTerm, selectedRegion, selectedType, selectedGender, selectiveSchools]);

  const totalPages = Math.ceil(filteredSchools.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedSchools = filteredSchools.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRegion, selectedType, selectedGender]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'fully_selective':
        return <Star className="h-4 w-4" />;
      case 'partially_selective':
        return <TrendingUp className="h-4 w-4" />;
      case 'agricultural':
        return <School className="h-4 w-4" />;
      default:
        return <GraduationCap className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'fully_selective':
        return 'bg-primary text-primary-foreground';
      case 'partially_selective':
        return 'bg-secondary text-secondary-foreground';
      case 'agricultural':
        return 'bg-accent text-accent-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getGenderIcon = (gender: string) => {
    switch (gender) {
      case 'boys':
        return '♂';
      case 'girls':
        return '♀';
      case 'coeducational':
        return '⚡';
      default:
        return '👥';
    }
  };

  const SchoolCard = ({ school }: { school: SelectiveSchool }) => (
    <Card className="h-full hover:shadow-lg transition-all duration-300 border-border group hover:border-primary/20">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg leading-tight text-foreground group-hover:text-primary transition-colors">
            {school.name}
          </CardTitle>
          <div className="flex flex-col gap-1">
            <Badge className={`shrink-0 text-xs ${getTypeColor(school.type)}`}>
              {getTypeIcon(school.type)}
              <span className="ml-1 capitalize">{school.type.replace('_', ' ')}</span>
            </Badge>
            <Badge variant="outline" className="text-xs">
              {getGenderIcon(school.gender)} {school.gender}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-2 text-muted-foreground">
          <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">{school.location}</p>
            <p className="text-xs text-muted-foreground">{school.region}</p>
          </div>
        </div>
        
        {school.established_year && (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm text-muted-foreground">
              Established {school.established_year}
            </span>
          </div>
        )}

        {school.subjects && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">Key Subjects</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {school.subjects.slice(0, 3).map((subject, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {subject}
                </Badge>
              ))}
              {school.subjects.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{school.subjects.length - 3} more
                </Badge>
              )}
            </div>
          </div>
        )}

        {school.notable_features && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-foreground">Notable Features</span>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1">
              {school.notable_features.slice(0, 2).map((feature, index) => (
                <li key={index} className="flex items-start gap-1">
                  <span className="text-accent">•</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ListItem = ({ school }: { school: SelectiveSchool }) => (
    <Card className="hover:shadow-md transition-shadow border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="font-semibold text-foreground">{school.name}</h3>
              <Badge className={`${getTypeColor(school.type)} text-xs`}>
                {getTypeIcon(school.type)}
                <span className="ml-1">{school.type.replace('_', ' ')}</span>
              </Badge>
              <Badge variant="outline" className="text-xs">
                {getGenderIcon(school.gender)} {school.gender}
              </Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {school.location}, {school.region}
              </div>
              {school.established_year && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Est. {school.established_year}
                </div>
              )}
            </div>
          </div>
          {school.subjects && (
            <div className="flex flex-wrap gap-1 max-w-xs">
              {school.subjects.slice(0, 3).map((subject, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {subject}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="w-full space-y-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading selective schools...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* SEO Optimized Header */}
      <div className="text-center space-y-6 bg-gradient-to-br from-primary/5 to-accent/5 rounded-3xl p-8">
        <div className="space-y-4">
          <Badge className="bg-primary/10 text-primary px-4 py-2 text-lg rounded-2xl">
            NSW Selective Schools 2025
          </Badge>
          <h1 className="text-3xl lg:text-5xl font-heading font-bold text-foreground leading-tight">
            Complete List of <span className="text-primary">NSW Selective Schools</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
            Discover all {selectiveSchools.length} selective high schools in NSW. Find fully selective, partially selective, and agricultural high schools with detailed information to help your child's academic journey.
          </p>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
          <div className="bg-white/80 rounded-2xl p-4 backdrop-blur-sm">
            <div className="text-2xl font-bold text-primary">{selectiveSchools.length}</div>
            <div className="text-sm text-muted-foreground">Total Schools</div>
          </div>
          <div className="bg-white/80 rounded-2xl p-4 backdrop-blur-sm">
            <div className="text-2xl font-bold text-accent">{regions.length}</div>
            <div className="text-sm text-muted-foreground">Regions</div>
          </div>
          <div className="bg-white/80 rounded-2xl p-4 backdrop-blur-sm">
            <div className="text-2xl font-bold text-primary">{selectiveSchools.filter(s => s.type === 'fully_selective').length}</div>
            <div className="text-sm text-muted-foreground">Fully Selective</div>
          </div>
          <div className="bg-white/80 rounded-2xl p-4 backdrop-blur-sm">
            <div className="text-2xl font-bold text-accent">{selectiveSchools.filter(s => s.gender === 'coeducational').length}</div>
            <div className="text-sm text-muted-foreground">Coeducational</div>
          </div>
        </div>
      </div>

      {/* Advanced Search and Filters */}
      <Card className="bg-card shadow-lg">
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold text-foreground">Find Your Perfect Selective School</h2>
          </div>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search schools, locations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger>
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger>
                <SelectValue placeholder="School type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="fully_selective">Fully Selective</SelectItem>
                <SelectItem value="partially_selective">Partially Selective</SelectItem>
                <SelectItem value="agricultural">Agricultural</SelectItem>
              </SelectContent>
            </Select>

            <Select value={selectedGender} onValueChange={setSelectedGender}>
              <SelectTrigger>
                <SelectValue placeholder="Gender type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                <SelectItem value="coeducational">Coeducational</SelectItem>
                <SelectItem value="boys">Boys Only</SelectItem>
                <SelectItem value="girls">Girls Only</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button
                variant={view === 'grid' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('grid')}
                className="flex-1"
              >
                Grid
              </Button>
              <Button
                variant={view === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setView('list')}
                className="flex-1"
              >
                List
              </Button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground">
              Showing <span className="font-medium text-foreground">{filteredSchools.length}</span> of {selectiveSchools.length} selective schools
            </p>
            {filteredSchools.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => {
                setSearchTerm('');
                setSelectedRegion('all');
                setSelectedType('all');
                setSelectedGender('all');
              }}>
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="space-y-6">
        {filteredSchools.length === 0 ? (
          <Card className="bg-muted/50">
            <CardContent className="p-12 text-center">
              <School className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">No schools found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search criteria or clearing the filters.
              </p>
              <Button onClick={() => {
                setSearchTerm('');
                setSelectedRegion('all');
                setSelectedType('all');
                setSelectedGender('all');
              }}>
                Clear All Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {view === 'grid' ? (
              <>
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {paginatedSchools.map((school, index) => (
                    <SchoolCard key={`${school.name}-${index}`} school={school} />
                  ))}
                </div>
                
                {/* Pagination for Grid View */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-6">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(prev => Math.max(prev - 1, 1));
                            }}
                            className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                          />
                        </PaginationItem>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(page);
                              }}
                              isActive={currentPage === page}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        
                        <PaginationItem>
                          <PaginationNext 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(prev => Math.min(prev + 1, totalPages));
                            }}
                            className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            ) : (
              <>
                <Card className="bg-card">
                  <ScrollArea className="h-[600px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[300px]">School Name</TableHead>
                          <TableHead className="w-[150px]">Location</TableHead>
                          <TableHead className="w-[150px]">Region</TableHead>
                          <TableHead className="w-[120px]">Type</TableHead>
                          <TableHead className="w-[100px]">Gender</TableHead>
                          <TableHead className="w-[100px] text-right">Established</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedSchools.map((school, index) => (
                          <TableRow key={`${school.name}-${index}`} className="hover:bg-muted/50">
                            <TableCell className="font-medium">{school.name}</TableCell>
                            <TableCell className="text-muted-foreground">{school.location}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {school.region}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={`${getTypeColor(school.type)} text-xs`}>
                                {getTypeIcon(school.type)}
                                <span className="ml-1">{school.type.replace('_', ' ')}</span>
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {getGenderIcon(school.gender)} {school.gender}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {school.established_year || 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </Card>

                {/* Pagination for Table View */}
                {totalPages > 1 && (
                  <div className="flex justify-center mt-6">
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(prev => Math.max(prev - 1, 1));
                            }}
                            className={currentPage <= 1 ? "pointer-events-none opacity-50" : ""}
                          />
                        </PaginationItem>
                        
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setCurrentPage(page);
                              }}
                              isActive={currentPage === page}
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        
                        <PaginationItem>
                          <PaginationNext 
                            href="#" 
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(prev => Math.min(prev + 1, totalPages));
                            }}
                            className={currentPage >= totalPages ? "pointer-events-none opacity-50" : ""}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* SEO Footer Content */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5">
        <CardContent className="p-8">
          <div className="text-center space-y-4">
            <h2 className="text-2xl font-heading font-bold text-foreground">
              Need Help with Selective School Preparation?
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Schoolola offers expert selective school preparation courses with proven track records. Our specialised TMRW™ methodology has helped thousands of students gain entry into their preferred selective schools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90">
                Book Free Assessment
              </Button>
              <Button variant="outline" size="lg">
                Learn About Our Programs
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Legal Disclaimer */}
      <div className="mt-8 text-center">
        <p className="text-xs text-muted-foreground/70 max-w-3xl mx-auto leading-relaxed">
          <strong>Disclaimer:</strong> This list of NSW Selective Schools is current as of August 2025 and is based on our research and understanding of publicly available information. School details, locations, admission criteria, and availability may change. Please verify current information directly with the NSW Department of Education or individual schools before making decisions. Schoolola is not responsible for any inaccuracies or changes to school information.
        </p>
      </div>
    </div>
  );
};

export default SelectiveSchoolsFinder;